import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $createTableNode,
  $createTableRowNode,
  $createTableCellNode,
  TableCellHeaderStates,
} from '@lexical/table';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isParagraphNode,
} from 'lexical';
import { useCallback, useEffect } from 'react';

import { TABLE_ROW_REGEX, TABLE_SEPARATOR_REGEX } from '../utils';
// Helper function to parse a table row
const parseTableRow = (rowText: string): string[] | null => {
  const rowMatch = rowText.match(TABLE_ROW_REGEX);
  if (!rowMatch) return null;

  const cellsText = rowMatch[1];
  const cells = cellsText.split('|').map(cell => cell.trim());
  return cells.length > 0 ? cells : null;
};

// Helper function to create a table cell
const createTableCell = (cellText: string, isHeader: boolean) => {
  const tableCellNode = $createTableCellNode(
    isHeader ? TableCellHeaderStates.ROW : TableCellHeaderStates.NO_STATUS,
  );

  if (cellText) {
    const paragraphNode = $createParagraphNode();
    paragraphNode.append($createTextNode(cellText));
    tableCellNode.append(paragraphNode);
  }

  return tableCellNode;
};

// Helper function to create a table from parsed row data
const createTableFromRows = (rowsData: string[][]): any => {
  const tableNode = $createTableNode();

  rowsData.forEach((cells, rowIndex) => {
    const tableRowNode = $createTableRowNode();
    const isHeader = rowIndex === 0;

    cells.forEach(cellText => {
      const tableCellNode = createTableCell(cellText, isHeader);
      tableRowNode.append(tableCellNode);
    });

    tableNode.append(tableRowNode);
  });

  return tableNode;
};

const MarkdownTablePlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();

  const convertMarkdownTable = useCallback(() => {
    editor.update(() => {
      const root = $getRoot();
      const children = root.getChildren();

      // Look for consecutive table-like paragraphs
      for (let i = 0; i < children.length; i++) {
        const node = children[i];
        if (!$isParagraphNode(node)) {
          continue;
        }

        const text = node.getTextContent().trim();

        // Check if this is a multiline table (contains newlines and table rows)
        const lines = text
          .split('\n')
          .map(line => line.trim())
          .filter(line => line);

        // Check if all lines are table rows (excluding separator rows)
        const tableLines = lines.filter(
          line =>
            TABLE_ROW_REGEX.test(line) && !TABLE_SEPARATOR_REGEX.test(line),
        );

        // If we have multiple table data rows, and all lines are either data rows or separators, create table
        const allLinesAreTableRelated = lines.every(
          line =>
            TABLE_ROW_REGEX.test(line) || TABLE_SEPARATOR_REGEX.test(line),
        );

        if (tableLines.length >= 2 && allLinesAreTableRelated) {
          // Parse all table rows
          const rowsData: string[][] = [];

          for (const lineText of tableLines) {
            const cells = parseTableRow(lineText);
            if (cells) {
              rowsData.push(cells);
            }
          }

          if (rowsData.length > 0) {
            // Create table node using helper function
            const tableNode = createTableFromRows(rowsData);

            // Insert table before removing the node to maintain DOM structure
            // Insert the table right before the current node
            node.insertBefore(tableNode);

            // Now safely remove the original paragraph node
            node.remove();

            // Skip to next node since we processed this one
            continue;
          }
        }

        // Legacy single-line check (keeping for compatibility)
        if (!TABLE_ROW_REGEX.test(text)) {
          continue;
        }

        // Found a potential table row, look for more consecutive rows
        const tableRows: { node: any; text: string }[] = [{ node, text }];
        let j = i + 1;

        while (j < children.length) {
          const nextNode = children[j];
          if (!$isParagraphNode(nextNode)) break;

          const nextText = nextNode.getTextContent().trim();
          if (
            !TABLE_ROW_REGEX.test(nextText) &&
            !TABLE_SEPARATOR_REGEX.test(nextText)
          ) {
            break;
          }

          if (TABLE_ROW_REGEX.test(nextText)) {
            tableRows.push({ node: nextNode, text: nextText });
          }
          j++;
        }

        // Need at least 2 rows to make a table
        if (tableRows.length < 2) {
          continue;
        }
        // Parse all table rows
        const rowsData: string[][] = [];

        for (const row of tableRows) {
          const cells = parseTableRow(row.text);
          if (cells) {
            rowsData.push(cells);
          }
        }

        // Create table node using helper function
        const tableNode = createTableFromRows(rowsData);

        // Insert table before the first row, then remove all rows
        const firstRowNode = tableRows[0].node;
        firstRowNode.insertBefore(tableNode);

        // Remove original nodes after inserting table
        for (const row of tableRows) {
          row.node.remove();
        }

        // Skip past the rows we just processed
        i = j - 1;
      }
    });
  }, [editor]);

  // Convert tables when editor content changes (handles initial load + updates)
  useEffect(() => {
    return mergeRegister(
      // This handles both initial content and programmatic updates
      editor.registerUpdateListener(({ editorState, tags }) => {
        // Only process if there are actual content changes (not just selection changes)
        if (tags.has('historic') || tags.has('collaboration')) {
          return; // Skip historic or collaborative updates
        }

        // Check if there's actually content to process before running conversion
        editorState.read(() => {
          const root = $getRoot();
          const hasTextContent = root.getTextContent().trim().length > 0;
          if (hasTextContent) {
            convertMarkdownTable(); // No delay needed - content has already changed
          }
        });
      }),

      // Alternative: Use text content listener for more semantic handling
      editor.registerTextContentListener(textContent => {
        // Only run if there's meaningful content that could contain tables
        if (textContent.includes('|') && textContent.trim().length > 0) {
          convertMarkdownTable(); // No delay needed - text content has already changed
        }
      }),
    );
  }, [editor, convertMarkdownTable]);

  return null;
};

export { MarkdownTablePlugin };
